import { idDoPadrao, padraoSvg, type EstiloDoCorteNoPapel } from './tramaNoPapel'
import {
  annotationSizeMm,
  ANNOT_COLOR,
  ANNOT_LAYER,
} from './annotations'
import { arcSweep, walkPath } from './arcFit'
import {
  dimensionGeometry,
  dimensionSizeMm,
  DIM_COLOR,
  DIM_DASH_MM,
  DIM_LAYER,
  DIM_TICK_MM,
  DIM_WIDTH_MM,
} from './dimensions'
import type { Drawing2D, DrawingLayer, Polyline2D } from './viewTypes'

// ---------------------------------------------------------------------------
// Writer SVG.
//
// Unidade do SVG = MILÍMETRO DE PAPEL à escala do desenho. Ou seja,
// 1 m de modelo -> 1000/scaleDenom unidades. Consequências boas:
//  - as espessuras de traço (0.35 / 0.13) são directamente milímetros,
//    como nas normas de desenho;
//  - o `width`/`height` em mm faz o browser imprimir à escala certa;
//  - o viewBox é sempre 0 0 W H (canto superior esquerdo na origem).
//
// O eixo Y do SVG cresce para BAIXO, o do desenho cresce para cima:
// invertem-se as ordenadas na transformação (nada de `transform`
// aninhado, que estragava a espessura do traço se houvesse escala).
//
// Determinístico: mesma entrada, mesma string (nada de datas, ids
// aleatórios ou iteração por Set).
// ---------------------------------------------------------------------------

export interface SvgOptions {
  /** Espessura do traço de corte, em mm de papel. */
  cutWidthMm?: number
  /** Espessura do traço de projecção, em mm de papel. */
  projWidthMm?: number
  /** Quanto a cor da projecção se aproxima do branco. 0 = cor cheia (a
   *  projecção lê-se tanto como o corte), 1 = branco. Por omissão
   *  `SVG_PROJ_FADE`. Ver `lineWeights.ts`. */
  projFade?: number
  /** Margem à volta do desenho, em mm de papel. */
  marginMm?: number
  /** Cor do corte quando a camada não traz cor. */
  cutColor?: string
  /** Cor de fundo; null (por omissão) = fundo transparente. */
  background?: string | null
  /** Casas decimais das coordenadas. */
  decimals?: number
  /** Preenche (poché) os contornos FECHADOS do corte. Por omissão
   *  DESLIGADO no writer: quem quer poché pede-o (a UI pede sempre). */
  fillCuts?: boolean
  /** O estilo do corte de cada elemento (fundo e trama), o mesmo do 3D.
   *  Uma camada com cor de preenchimento escolhida à mão fica com ela. */
  estiloDoCorte?: EstiloDoCorteNoPapel
  /** Escreve `data-local-id` nos caminhos, para a pré-visualização poder
   *  ligar um clique ao elemento do modelo. Desligado por omissão: o SVG
   *  exportado não tem de carregar identificadores internos. */
  localIds?: boolean
  /** Cor do texto das anotações. */
  annotColor?: string
  /** Cor do traço e do texto das cotas. */
  dimColor?: string
}

export const SVG_CUT_WIDTH_MM = 0.35
export const SVG_PROJ_WIDTH_MM = 0.13
/** Quanto a projecção se aproxima do branco em relação ao corte. Era
 *  0,55, que sobre papel branco dava cinzentos de contraste ~2,4:1 (a
 *  linha de projecção quase desaparecia num traço de 0,13 mm). Com 0,40
 *  fica em ~3,4:1 e continua claramente mais leve do que o corte. */
export const SVG_PROJ_FADE = 0.4
/** Quanto o preenchimento do corte (poché) se aproxima do branco em
 *  relação à cor do traço de corte. 0,85 é exactamente o que se vê se se
 *  pintasse a cor da camada com alfa 0,15 sobre papel branco, mas fica
 *  OPACO: o PDF e o DXF não precisam de transparência (no PDF a alfa
 *  obrigaria a um /ExtGState) e o resultado é idêntico em todos os
 *  formatos. Dá uma mancha que se lê como cheio sem tapar o traço. */
export const SVG_FILL_FADE = 0.85
/** Margem por omissão à volta do desenho, em mm de papel. Exportada
 *  porque a fase 2 precisa de reproduzir a mesma transformação para
 *  alinhar o overlay interactivo com o SVG. */
export const SVG_DEFAULT_MARGIN_MM = 5

export function drawingToSvg(d: Drawing2D, opts?: SvgOptions): string {
  const cutW = opts?.cutWidthMm ?? SVG_CUT_WIDTH_MM
  const projW = opts?.projWidthMm ?? SVG_PROJ_WIDTH_MM
  const margin = opts?.marginMm ?? SVG_DEFAULT_MARGIN_MM
  const decimals = opts?.decimals ?? 3
  const cutColor = opts?.cutColor ?? '#111111'
  const annotColor = opts?.annotColor ?? ANNOT_COLOR
  const bg = opts?.background ?? null
  // mm de papel por metro de modelo.
  const k = 1000 / (d.scaleDenom || 100)

  const w = Math.max((d.bbox.maxX - d.bbox.minX) * k, 0) + margin * 2
  const h = Math.max((d.bbox.maxY - d.bbox.minY) * k, 0) + margin * 2

  // Numéricas primeiro: as cotas precisam de somar afastamentos em mm de
  // papel ao ponto já convertido, e formatar cedo obrigava a voltar a
  // ler o número da string.
  const toX = (x: number): number => (x - d.bbox.minX) * k + margin
  const toY = (y: number): number => (d.bbox.maxY - y) * k + margin
  const fx = (x: number): string => fmt(toX(x), decimals)
  const fy = (y: number): string => fmt(toY(y), decimals)

  const pathOf = (pl: Polyline2D): string =>
    polylinePathData(pl, { fx, fy, k, decimals })

  const lines: string[] = []
  lines.push(
    `<svg xmlns="http://www.w3.org/2000/svg" width="${fmt(w, decimals)}mm"` +
      ` height="${fmt(h, decimals)}mm" viewBox="0 0 ${fmt(w, decimals)} ${fmt(
        h,
        decimals,
      )}" data-name="${esc(d.name)}" data-scale="1:${d.scaleDenom}">`,
  )
  if (bg) {
    lines.push(
      `<rect x="0" y="0" width="${fmt(w, decimals)}" height="${fmt(
        h,
        decimals,
      )}" fill="${esc(bg)}"/>`,
    )
  }
  const idAttr = (pl: Polyline2D): string =>
    opts?.localIds && typeof pl.localId === 'number'
      ? ` data-local-id="${pl.localId}"`
      : ''

  // Passagem 1: os preenchimentos de TODAS as camadas primeiro. Se cada
  // camada pintasse a sua mancha logo a seguir aos seus traços, a mancha
  // da camada seguinte tapava as linhas da anterior.
  if (opts?.fillCuts) {
    // Os padrões das tramas entram num <defs> antes das manchas.
    const ondeVaoOsPadroes = lines.length
    const padroes = new Map<string, string>()
    for (const layer of d.layers) {
      const groups = fillGroups(layer)
      if (groups.length === 0) continue
      const fillDaCamada = layerFill(layer, cutColor)
      const camadaEscolhida = normalizeHex(layer.fillHex) !== undefined
      lines.push(`<g id="${esc(layer.name)}_FILL" stroke="none">`)
      for (const grp of groups) {
        const dAttr = grp.polys.map(pathOf).filter(Boolean).join(' ')
        if (!dAttr) continue
        let fill = fillDaCamada
        const estilo = !camadaEscolhida && opts?.estiloDoCorte ? opts.estiloDoCorte(grp.localId, layer.name) : null
        if (estilo) {
          const padrao = padraoSvg(estilo, esc)
          if (padrao) {
            padroes.set(idDoPadrao(estilo), padrao)
            fill = `url(#${idDoPadrao(estilo)})`
          } else {
            fill = esc(estilo.fundo)
          }
        }
        // fill-rule="evenodd": os contornos do mesmo elemento vão todos
        // no MESMO caminho, por isso um anel dentro de outro (o vão de
        // uma janela dentro da parede) fica buraco em vez de mancha.
        lines.push(
          `<path class="poche" d="${dAttr}" fill="${fill}" fill-rule="evenodd"${
            opts?.localIds && typeof grp.localId === 'number'
              ? ` data-local-id="${grp.localId}"`
              : ''
          }/>`,
        )
      }
      lines.push('</g>')
    }
    if (padroes.size > 0) lines.splice(ondeVaoOsPadroes, 0, `<defs>${[...padroes.values()].join('')}</defs>`)
  }

  for (const layer of d.layers) {
    const colors = layerColors(layer, cutColor, opts?.projFade)
    lines.push(`<g id="${esc(layer.name)}" fill="none" stroke-linecap="round" stroke-linejoin="round">`)
    // Projecção primeiro: o corte fica por cima.
    for (const pl of layer.projection) {
      const dAttr = pathOf(pl)
      if (!dAttr) continue
      lines.push(
        `<path class="proj" d="${dAttr}" stroke="${colors.proj}" stroke-width="${fmt(
          projW,
          3,
        )}"${idAttr(pl)}/>`,
      )
    }
    for (const pl of layer.cut) {
      const dAttr = pathOf(pl)
      if (!dAttr) continue
      lines.push(
        `<path class="cut" d="${dAttr}" stroke="${colors.cut}" stroke-width="${fmt(
          cutW,
          3,
        )}"${idAttr(pl)}/>`,
      )
    }
    lines.push('</g>')
  }

  // Cotas: linhas de cota, traços de extensão e o valor rodado sobre a
  // linha. As grandezas de PAPEL (altura do texto, traços, afastamentos)
  // vão directas, porque a unidade do SVG É o mm de papel. O eixo Y está
  // invertido, portanto uma direcção (nx, ny) do desenho aparece aqui
  // como (nx, -ny) e a rotação troca de sinal.
  const dims = d.dimensions ?? []
  if (dims.length > 0) {
    const dimColor = opts?.dimColor ?? DIM_COLOR
    lines.push(
      `<g id="${esc(DIM_LAYER)}" stroke="${esc(dimColor)}" fill="none"` +
        ' stroke-linecap="round" font-family="Helvetica, Arial, sans-serif">',
    )
    for (const dim of dims) {
      const g = dimensionGeometry(dim)
      const size = dimensionSizeMm(dim)
      const w = fmt(DIM_WIDTH_MM, 3)
      for (const ln of g.lines) {
        lines.push(
          `<line class="dim" x1="${fx(ln.a[0])}" y1="${fy(ln.a[1])}" x2="${fx(
            ln.b[0],
          )}" y2="${fy(ln.b[1])}" stroke-width="${w}"/>`,
        )
      }
      for (const ln of g.dashed) {
        lines.push(
          `<line class="dim-area" x1="${fx(ln.a[0])}" y1="${fy(
            ln.a[1],
          )}" x2="${fx(ln.b[0])}" y2="${fy(ln.b[1])}" stroke-width="${w}"` +
            ` stroke-dasharray="${fmt(DIM_DASH_MM[0], 3)} ${fmt(
              DIM_DASH_MM[1],
              3,
            )}"/>`,
        )
      }
      for (const t of g.ticks) {
        const cx = toX(t.at[0])
        const cy = toY(t.at[1])
        lines.push(
          `<line class="dim-tick" x1="${fmt(
            cx - t.nx * DIM_TICK_MM,
            decimals,
          )}" y1="${fmt(cy + t.ny * DIM_TICK_MM, decimals)}" x2="${fmt(
            cx + t.nx * DIM_TICK_MM,
            decimals,
          )}" y2="${fmt(cy - t.ny * DIM_TICK_MM, decimals)}"` +
            ` stroke-width="${w}"/>`,
        )
      }
      for (const t of g.texts) {
        const tx = toX(t.at[0]) + t.nx * t.offsetMm
        const ty = toY(t.at[1]) - t.ny * t.offsetMm
        const deg = (-t.angleRad * 180) / Math.PI
        const rot =
          Math.abs(deg) < 1e-9
            ? ''
            : ` transform="rotate(${fmt(deg, 3)} ${fmt(tx, decimals)} ${fmt(
                ty,
                decimals,
              )})"`
        lines.push(
          `<text class="dim-text" x="${fmt(tx, decimals)}" y="${fmt(
            ty,
            decimals,
          )}" font-size="${fmt(size, 3)}" text-anchor="middle" fill="${esc(
            dimColor,
          )}" stroke="none"${rot}>${esc(t.text)}</text>`,
        )
      }
    }
    lines.push('</g>')
  }

  // Anotações por último: texto sobre o desenho, nunca por baixo. A
  // unidade do SVG é o mm de papel, portanto a cota do texto vai
  // directa. `dominant-baseline` fica de fora de propósito (suporte
  // irregular nos visualizadores de SVG): a âncora é a linha de base,
  // como no PDF e no DXF.
  const notes = d.annotations ?? []
  if (notes.length > 0) {
    lines.push(
      `<g id="${esc(ANNOT_LAYER)}" fill="${esc(annotColor)}" stroke="none"` +
        ' font-family="Helvetica, Arial, sans-serif">',
    )
    for (const a of notes) {
      lines.push(
        `<text class="annot" x="${fx(a.x)}" y="${fy(a.y)}" font-size="${fmt(
          annotationSizeMm(a),
          3,
        )}">${esc(a.text)}</text>`,
      )
    }
    lines.push('</g>')
  }
  lines.push('</svg>')
  return lines.join('\n')
}

/** Como converter um ponto do desenho em coordenadas do caminho SVG.
 *  `k` é quanto vale um metro do desenho nessas coordenadas (o raio dos
 *  arcos precisa dele). */
export interface PathScribe {
  fx: (x: number) => string
  fy: (y: number) => string
  k: number
  decimals: number
}

/** Caminho SVG (`d`) de uma polilinha, com os arcos e os círculos do IR.
 *
 *  O eixo Y do SVG está invertido em relação ao desenho, portanto um
 *  arco directo (anti-horário) no desenho aparece no SVG a percorrer
 *  ângulos DECRESCENTES: a bandeira sweep vem ao contrário. Todos os
 *  mapeamentos que passam por aqui (o papel e o overlay da
 *  pré-visualização) partilham essa inversão, por isso ela está aqui
 *  dentro e não é opção de quem chama.
 *
 *  Exportado porque o overlay interactivo precisa de desenhar as MESMAS
 *  linhas realçadas: escrever um segundo tradutor de arcos era a
 *  maneira certa de o realce um dia deixar de coincidir com o desenho. */
export function polylinePathData(pl: Polyline2D, s: PathScribe): string {
  const pts = pl.points
  if (!pts || pts.length < 2) return ''
  const { fx, fy, k, decimals } = s
  if (pl.circle) {
    const { cx, cy, r } = pl.circle
    const rr = fmt(r * k, decimals)
    // Dois semicírculos: o `A` não consegue ir de um ponto para ele
    // próprio (o SVG ignora um arco de comprimento nulo).
    return (
      `M ${fx(cx + r)} ${fy(cy)}` +
      ` A ${rr} ${rr} 0 1 0 ${fx(cx - r)} ${fy(cy)}` +
      ` A ${rr} ${rr} 0 1 0 ${fx(cx + r)} ${fy(cy)} Z`
    )
  }
  const walk = walkPath(pl)
  if (!walk) return ''
  let out = `M ${fx(walk.from[0])} ${fy(walk.from[1])}`
  for (const step of walk.steps) {
    if (step.kind === 'arc') {
      const rr = fmt(step.arc.r * k, decimals)
      const large = arcSweep(step.arc) > Math.PI ? 1 : 0
      const sweep = step.arc.ccw ? 0 : 1
      out += ` A ${rr} ${rr} 0 ${large} ${sweep} ${fx(step.to[0])} ${fy(
        step.to[1],
      )}`
    } else {
      out += ` L ${fx(step.to[0])} ${fy(step.to[1])}`
    }
  }
  if (pl.closed) out += ' Z'
  return out
}

/** Grupo de contornos fechados que se preenchem JUNTOS (um caminho só,
 *  regra even-odd). Um grupo por elemento do modelo; os contornos sem
 *  elemento conhecido ficam cada um por si (sem localId não há como
 *  saber quem é furo de quem, e juntá-los ao calhas apagaria manchas
 *  legítimas que se sobrepõem). */
export interface FillGroup {
  localId?: number
  polys: Polyline2D[]
}

/** Agrupa os contornos FECHADOS do corte de uma camada por elemento.
 *  Determinístico: a ordem dos grupos é a ordem de aparecimento. */
export function fillGroups(layer: DrawingLayer): FillGroup[] {
  const byId = new Map<number, FillGroup>()
  const out: FillGroup[] = []
  for (const pl of layer.cut) {
    if (!pl.closed || pl.points.length < 3) continue
    if (typeof pl.localId !== 'number') {
      out.push({ polys: [pl] })
      continue
    }
    const g = byId.get(pl.localId)
    if (g) {
      g.polys.push(pl)
    } else {
      const created: FillGroup = { localId: pl.localId, polys: [pl] }
      byId.set(pl.localId, created)
      out.push(created)
    }
  }
  return out
}

/** Cor do preenchimento (poché) de uma camada: a declarada, ou a cor do
 *  corte aproximada do branco. */
export function layerFill(layer: DrawingLayer, fallback: string): string {
  const declared = normalizeHex(layer.fillHex)
  if (declared) return declared
  return fadeHex(normalizeHex(layer.colorHex) ?? fallback, SVG_FILL_FADE)
}

/** Cor do corte e cor esbatida da projecção de uma camada. `fade` 0 dá
 *  uma projecção com a mesma cor do corte (vistas em que a projecção é o
 *  assunto: ver `lineWeights.ts`). */
export function layerColors(
  layer: DrawingLayer,
  fallback: string,
  fade?: number,
): { cut: string; proj: string } {
  const cut = normalizeHex(layer.colorHex) ?? fallback
  const amount = typeof fade === 'number' && fade >= 0 ? fade : SVG_PROJ_FADE
  return { cut, proj: amount > 0 ? fadeHex(cut, amount) : cut }
}

/** Aproxima a cor ao branco (traço de projecção mais leve). `amount` 0
 *  devolve a cor original, 1 devolve branco. */
export function fadeHex(hex: string, amount: number): string {
  const c = normalizeHex(hex)
  if (!c) return '#999999'
  const a = Math.min(Math.max(amount, 0), 1)
  const n = Number.parseInt(c.slice(1), 16)
  const r = Math.round(((n >> 16) & 0xff) + (255 - ((n >> 16) & 0xff)) * a)
  const g = Math.round(((n >> 8) & 0xff) + (255 - ((n >> 8) & 0xff)) * a)
  const b = Math.round((n & 0xff) + (255 - (n & 0xff)) * a)
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`
}

/** Normaliza um hex CSS para #rrggbb minúsculo; undefined se inválido. */
export function normalizeHex(hex: string | undefined): string | undefined {
  if (typeof hex !== 'string') return undefined
  let h = hex.trim()
  if (h.startsWith('#')) h = h.slice(1)
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2]
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return undefined
  return `#${h.toLowerCase()}`
}

/** Número com casas fixas, sem zeros nem ponto pendurados. */
export function fmt(v: number, decimals: number): string {
  if (!Number.isFinite(v)) return '0'
  let s = v.toFixed(decimals)
  if (s.includes('.')) s = s.replace(/0+$/, '').replace(/\.$/, '')
  return s === '-0' ? '0' : s
}

function esc(s: string): string {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}
